import { Button } from 'primereact/button';
import { useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import FormControlledInput from '../../../../components/inputs/FormInputs/FormControlledInput';
import FormControlledNumberInput from '../../../../components/inputs/FormInputs/FormControlledNumberInput';
import WrapperButton from '../../../../components/buttons/WrapperButton';
import { COFormFields } from '../../../../Interfaces/Accounting/ACForms.interfaces';
import PendingAmountMessage from './PendingAmountMessage';
import PhaseRow from './PhaseRow';

type LineRowProps = {
  fieldIndex: number;
  job: string;
  remove: (index: number) => void;
  costTypes: Record<number, string>;
  ums: Record<string, string>;
};

const LineRow = ({ fieldIndex, job, remove, costTypes, ums }: LineRowProps) => {
  const { control, getValues } = useFormContext<COFormFields>();
  const [onAmountFocus, setOnAmountFocus] = useState(false);
  const [pendingAmount, setPendingAmount] = useState<number>();
  const {
    fields: phases,
    append: appendPhase,
    remove: removePhase,
  } = useFieldArray<COFormFields>({
    control,
    name: `lines.${fieldIndex}.phases` as 'lines.0.phases',
  });

  const calculatePending = (amount: number) => {
    const lines = getValues('lines') ?? [];
    const total = getValues('totalAmount') ?? 0;

    const otherLines = lines.reduce((acc, line, index) => {
      if (index === fieldIndex) {
        return acc;
      }

      return acc + (line.amount ?? 0);
    }, 0);

    setPendingAmount(total - otherLines - amount);
  };

  return (
    <div className="col-12 grid p-0 mx-1">
      <div className="col-12 flex align-content-center gap-2">
        <label className="text-17px font-bold">SOV Item {fieldIndex + 1}</label>
        <WrapperButton onClick={() => remove(fieldIndex)} className="h-fit">
          <i className="pi pi-trash p-1 border-circle hover:bg-gray-300" />
        </WrapperButton>
      </div>
      <div className="col-12 sm:col-6">
        <label
          htmlFor={`lines.${fieldIndex}.description`}
          className="text-standard block mb-2"
        >
          Description
        </label>
        <FormControlledInput
          defaultValue=""
          formID={`lines.${fieldIndex}.description`}
          rules={{
            required: {
              value: true,
              message: 'Line Description is a required field',
            },
          }}
          autocomplete="off"
          isFieldArray={true}
        />
      </div>
      <div className="col-12 sm:col-6">
        <label
          htmlFor={`lines.${fieldIndex}.amount`}
          className="text-standard block mb-2"
        >
          Item Amount
        </label>
        <FormControlledNumberInput
          formID={`lines.${fieldIndex}.amount`}
          mode="currency"
          currency="USD"
          locale="en-US"
          rules={{
            required: {
              value: true,
              message: 'Line Amount is a required field',
            },
          }}
          isFieldArray={true}
          onFocus={() => {
            setOnAmountFocus(true);
            calculatePending(getValues(`lines.${fieldIndex}.amount`) ?? 0);
          }}
          onBlur={() => {
            setOnAmountFocus(false);
          }}
          onChange={(amount) => calculatePending((amount as number) ?? 0)}
        />
        {onAmountFocus && (
          <PendingAmountMessage
            pendingAmount={pendingAmount}
            targetAmount={getValues('totalAmount')}
            targetLabel="CO total amount"
          />
        )}
      </div>
      <div className="col-12 flex gap-3 align-content-center">
        <label className="text-standard h-fit my-auto font-bold">Job Cost Phases</label>
        <Button
          className="mr-auto text-17px py-2 px-3 font-bold"
          onClick={() =>
            appendPhase({
              description: '',
              code: null,
            })
          }
          type="button"
        >
          +
        </Button>
      </div>
      {phases.map((phase, index) => {
        return (
          <PhaseRow
            key={phase.id}
            fieldId={phase.id}
            fieldIndex={index}
            lineIndex={fieldIndex}
            job={job}
            remove={removePhase}
            costTypes={costTypes}
            ums={ums}
          />
        );
      })}
    </div>
  );
};

export default LineRow;
